import React, { useState } from "react";
import styled from "styled-components";
import { device } from "../lib/breakpoints";
import { Spacer } from "../styles/CommonElements";
import Button from "../components/Button";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../helpers/mixpanel";
import SEO from "../components/SEO";
import { APP_NAME } from "../helpers/constants";
import IntakeModal from "../components/IntakeModal";

const About = () => {
  const navigate = useNavigate();
  const [showIntakeModal, setShowIntakeModal] = useState(false);

  const handleStartTrial = () => {
    trackEvent("About Page - Start Trial Click");
    setShowIntakeModal(true);
  };

  const handleContactUs = () => {
    trackEvent("About Page - Contact Click");
    navigate("/contact");
  };

  const handleIntakeSubmit = async (profileData) => {
    try {
      console.log("Trial signup data submitted:", profileData);
      setShowIntakeModal(false);
      // IntakeModal handles the trial signup process
    } catch (error) {
      console.error("Error processing trial signup:", error);
    }
  };

  const handleCloseModal = () => {
    setShowIntakeModal(false);
  };

  const stats = [
    { number: "10K+", label: "Properties Managed" },
    { number: "30%", label: "Avg. Income Increase" },
    { number: "20+", label: "Hours Saved/Month" },
  ];

  const values = [
    {
      icon: "🔒",
      title: "Security First",
      description:
        "Bank-level security and compliance aren't afterthoughts - they're built into everything we do.",
    },
    {
      icon: "🎯",
      title: "Results-Focused",
      description:
        "We measure success by your success. Every feature is designed to save you time and increase your rental income.",
    },
    {
      icon: "⚡",
      title: "Always Innovating",
      description:
        "We're constantly improving our platform and adding new features based on landlord feedback.",
    },
    {
      icon: "🤝",
      title: "Partnership Mindset",
      description:
        "We're not just a vendor - we're your technology partner invested in your long-term success.",
    },
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Clumse",
    url: "https://www.clumse.com",
    logo: "https://www.clumse.com/assets/logo.png",
    founder: {
      "@type": "Person",
      name: "Bryan Edelman",
      jobTitle: "Founder & CEO",
      sameAs: ["https://www.linkedin.com/in/"],
    },
    sameAs: [
      "https://www.linkedin.com/company/clumse",
      "https://www.crunchbase.com/organization/clumse",
      "https://www.producthunt.com/products/clumse",
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@clumse.com",
        areaServed: "US",
      },
    ],
  };

  return (
    <Container>
      <SEO
        title={`About Us | ${APP_NAME} - Stop Carrying Your Rentals in Your Head`}
        description={`Learn about ${APP_NAME}'s mission to help self-managing landlords track decisions, follow-ups, and what happens next in one place - especially when automation isn't enough.`}
        keywords={`${APP_NAME} company, landlord decisions follow-ups, property management technology, landlord software company, about ${APP_NAME}, landlord cognitive load`}
        imageAlt={`${APP_NAME} team helping landlords reduce cognitive load and centralize decisions`}
        url={`https://www.clumse.com/about`}
        canonical={`https://www.clumse.com/about`}
        schema={organizationSchema}
      />

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            We're Building the Future of Portfolio & Door Management
          </HeroTitle>
          <HeroSubtitle>
            {APP_NAME} was founded by property management veterans who saw an
            opportunity to transform how landlords manage their properties and
            increase their rental income.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Stats Section */}
      <StatsSection>
        <StatsContainer>
          {stats.map((stat, index) => (
            <StatItem key={index}>
              <StatNumber>{stat.number}</StatNumber>
              <StatLabel>{stat.label}</StatLabel>
            </StatItem>
          ))}
        </StatsContainer>
      </StatsSection>

      {/* Mission Section */}
      <MissionSection>
        <ContentContainer>
          <SectionTitle>Our Mission</SectionTitle>
          <MissionText>
            Too many independent landlords struggle with time-consuming property
            management and inconsistent rental income. Tenants have questions
            about leases, want to see property details, and need convenient
            payment options - but most landlord websites fall short of
            delivering this experience.
          </MissionText>
          <MissionText>
            We built {APP_NAME} to solve this problem with a comprehensive
            platform that combines AI-powered tenant engagement, automated rent
            tracking, seamless lease management, and integrated payments - all
            while maintaining the highest standards of security compliance.
          </MissionText>
        </ContentContainer>
      </MissionSection>

      {/* Story Section */}
      <StorySection>
        <ContentContainer>
          <StoryGrid>
            <StoryContent>
              <SectionTitle>How We Started</SectionTitle>
              <StoryText>
                After working with dozens of property management companies, our
                founders noticed a consistent pattern: great landlords were
                struggling to manage their properties efficiently and attract
                quality tenants online.
              </StoryText>
              <StoryText>
                Traditional property management wasn't enough. Tenants needed to
                feel confident about their rental experience, understand lease
                terms, and have their questions answered instantly - 24/7.
              </StoryText>
              <StoryText>
                That's when we developed our first AI assistant specifically for
                property management. The results were immediate: landlords saw
                30% reduction in vacancies and 20+ hours saved per month.
              </StoryText>
              <StoryText>
                Today, {APP_NAME} serves over 500 independent landlords and
                property managers across North America, helping them manage
                their properties more efficiently and increase their NOI.
              </StoryText>
            </StoryContent>

            <StoryImageContainer>
              {/* Placeholder for team/office image */}
              <StoryImagePlaceholder>
                <ImageIcon>🏢</ImageIcon>
                <ImageText>{APP_NAME} Team</ImageText>
              </StoryImagePlaceholder>
            </StoryImageContainer>
          </StoryGrid>
        </ContentContainer>
      </StorySection>

      {/* Values Section */}
      <ValuesSection>
        <ContentContainer>
          <SectionTitle>Our Values</SectionTitle>
          <ValuesGrid>
            {values.map((value, index) => (
              <ValueCard key={index}>
                <ValueIcon>{value.icon}</ValueIcon>
                <ValueTitle>{value.title}</ValueTitle>
                <ValueDescription>{value.description}</ValueDescription>
              </ValueCard>
            ))}
          </ValuesGrid>
        </ContentContainer>
      </ValuesSection>

      {/* Founder Section */}
      <TeamSection>
        <ContentContainer>
          <SectionTitle>Founder</SectionTitle>
          <TeamGrid>
            <TeamMember>
              <TeamPhotoPlaceholder>
                <img
                  src="/images/founder-bryan.jpg"
                  alt="Bryan Edelman headshot"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              </TeamPhotoPlaceholder>
              <TeamName>Bryan Edelman</TeamName>
              <TeamRole>Founder & CEO</TeamRole>
              <TeamBio>
                Bryan founded Clumse to give independent landlords a
                Rental Command Center that automates leasing, rent collection,
                maintenance, and communication.
              </TeamBio>
              <SocialLinks>
                <a href="mailto:support@clumse.com">Email</a>
                <a
                  href="https://www.linkedin.com/company/clumse"
                  target="_blank"
                  rel="noreferrer"
                >
                  LinkedIn
                </a>
                <a
                  href="https://www.crunchbase.com/organization/clumse"
                  target="_blank"
                  rel="noreferrer"
                >
                  Crunchbase
                </a>
                <a
                  href="https://www.producthunt.com/products/clumse"
                  target="_blank"
                  rel="noreferrer"
                >
                  Product Hunt
                </a>
              </SocialLinks>
            </TeamMember>
          </TeamGrid>
        </ContentContainer>
      </TeamSection>

      {/* CTA Section */}
      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Transform Your Portfolio?</CTATitle>
          <CTASubtitle>
            Join landlords already using {APP_NAME} to grow their portfolio
          </CTASubtitle>
          <Spacer $marginTop="2rem" />
          <CTAButtons>
            <PrimaryButton onClick={handleStartTrial}>
              Start Free Trial
            </PrimaryButton>
            <SecondaryButton onClick={handleContactUs}>
              Contact Us
            </SecondaryButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>

      {showIntakeModal && (
        <IntakeModal
          isOpen={showIntakeModal}
          onClose={handleCloseModal}
          onSubmit={handleIntakeSubmit}
          marketingSignup
        />
      )}
    </Container>
  );
};

export default About;

// Styled Components
const Container = styled.div`
  width: 100%;
  background: var(--color-bg-primary);
`;

const HeroSection = styled.section`
  background: var(--gradient-hero);
  padding: 8rem 2rem 6rem;
  text-align: center;

  @media ${device.mobile} {
    padding: 6rem 1rem 4rem;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--color-text-light);
  margin-bottom: 1.5rem;
  font-family: "Inter", sans-serif;
  line-height: 1.2;

  @media ${device.mobile} {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Inter", sans-serif;
  line-height: 1.6;

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const StatsSection = styled.section`
  background: var(--color-bg-secondary);
  padding: 4rem 2rem;
  border-bottom: 1px solid var(--color-border-light);

  @media ${device.mobile} {
    padding: 3rem 1rem;
  }
`;

const StatsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  font-weight: 500;
`;

const MissionSection = styled.section`
  padding: 6rem 2rem;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 2rem;
  font-family: "Inter", sans-serif;
  text-align: center;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const MissionText = styled.p`
  font-size: 1.2rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.7;
  margin-bottom: 1.5rem;
  text-align: center;

  @media ${device.mobile} {
    font-size: 1.1rem;
  }
`;

const StorySection = styled.section`
  background: var(--color-bg-secondary);
  padding: 6rem 2rem;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const StoryContent = styled.div``;

const StoryText = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const StoryImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StoryImagePlaceholder = styled.div`
  width: 100%;
  max-width: 400px;
  height: 300px;
  background: var(--gradient-accent);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-medium);
`;

const ImageIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.7;
`;

const ImageText = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  margin: 0;
`;

const ValuesSection = styled.section`
  padding: 6rem 2rem;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const ValueCard = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  background: var(--color-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-medium);
  }
`;

const ValueIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const ValueTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
`;

const ValueDescription = styled.p`
  font-size: 1rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.6;
  margin: 0;
`;

const TeamSection = styled.section`
  background: var(--color-bg-secondary);
  padding: 6rem 2rem;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
`;

const TeamMember = styled.div`
  text-align: center;
  padding: 2rem;
  background: var(--color-bg-primary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: var(--shadow-light);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-medium);
  }
`;

const TeamPhotoPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  background: var(--gradient-accent);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem auto;
  border: 1px solid var(--color-border-light);
`;

const PhotoIcon = styled.div`
  font-size: 3rem;
  opacity: 0.7;
`;

const TeamName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 0.5rem;
  font-family: "Inter", sans-serif;
`;

const TeamRole = styled.h4`
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-primary);
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;
`;

const TeamBio = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  font-family: "Inter", sans-serif;
  line-height: 1.5;
  margin: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 0.75rem;
  a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 600;
  }
`;

const CTASection = styled.section`
  background: var(--gradient-primary);
  padding: 6rem 2rem;
  text-align: center;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--color-text-light);
  margin-bottom: 1rem;
  font-family: "Inter", sans-serif;

  @media ${device.mobile} {
    font-size: 2rem;
  }
`;

const CTASubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: "Inter", sans-serif;
  line-height: 1.6;
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--color-bg-secondary);
  color: var(--color-primary);
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-dark);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: var(--color-text-light);
  border: 2px solid rgba(255, 255, 255, 0.5);
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: var(--color-text-light);
  }
`;
